import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { AnonymousConsent } from '@spartacus/core';
import { combineLatest, Observable, OperatorFunction, Subscription } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { AnonymousConsentsService } from '../../../../core/anonymous-consents';
import { ConsentTemplate } from '../../../../core/model';
import { RoutingHelperService } from '../../../../core/routing';
import { SitePrefixService } from '../../../../core/site-context';
import { fadeInFadeOutAnimation } from '../../../../shared';

@Component({
  selector: 'py-anonymous-consent-management',
  templateUrl: './anonymous-consent-management.component.html',
  styleUrls: ['./anonymous-consent-management.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [fadeInFadeOutAnimation],
  standalone: false,
})
export class AnonymousConsentManagementComponent implements OnInit, OnDestroy {
  anonymousConsentTemplates$: Observable<ConsentTemplate[]>;
  anonymousConsents$: Observable<AnonymousConsent[]>;

  private subscriptions = new Subscription();

  private isLegalPage$: Observable<boolean> = this.routingHelperService.legalPage();

  showCloseButton$: Observable<boolean> = this.anonymousConsentsService.showCloseButton();
  bannerVisible$: Observable<boolean> = this.anonymousConsentsService
    .isBannerVisible()
    .pipe(this.hideOnLegalPage(this.isLegalPage$, this.showCloseButton$));
  settingsVisible$: Observable<boolean> = this.anonymousConsentsService
    .isSettingsVisible()
    .pipe(this.hideOnLegalPage(this.isLegalPage$, this.showCloseButton$));
  hasPageSitePrefix$: Observable<boolean>;

  constructor(
    private anonymousConsentsService: AnonymousConsentsService,
    private sitePrefixService: SitePrefixService,
    private routingHelperService: RoutingHelperService
  ) {}

  private hideOnLegalPage(
    isLegalPage$: Observable<boolean>,
    showCloseButton$: Observable<boolean>
  ): OperatorFunction<boolean, boolean> {
    // only show the consent banners on a legal page if they have a "close" button,
    // so that it is possible to legal pages before consenting
    // (having a "close" button should mean they have manually opened it, e.g. from the footer)
    return (visible$: Observable<boolean>): Observable<boolean> =>
      combineLatest([visible$, isLegalPage$, showCloseButton$]).pipe(
        map(([visible, isLegalPage, showCloseButton]) => visible && (!isLegalPage || showCloseButton))
      );
  }

  ngOnInit(): void {
    this.anonymousConsentTemplates$ = this.anonymousConsentsService.getTemplates();
    this.anonymousConsents$ = this.anonymousConsentsService.getConsents();
    this.hasPageSitePrefix$ = this.sitePrefixService.getActive().pipe(map((sitePrefix) => sitePrefix !== ''));
  }

  onViewSettings(): void {
    this.hideBanner();
    this.showSettings();
  }

  hideSettingsAndShowBanner(): void {
    this.hideSettings();
    this.showBanner();
  }

  onAllowAll(): void {
    this.subscriptions.add(
      this.anonymousConsentsService
        .giveAllConsents()
        .pipe(take(1))
        .subscribe(() => this.hideBanner())
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  hideBanner(): void {
    this.anonymousConsentsService.toggleBannerDismissed(true);
  }

  showBanner(): void {
    this.anonymousConsentsService.toggleBannerDismissed(false);
  }

  showSettings() {
    this.anonymousConsentsService.toggleSettingsVisible(true);
  }

  hideSettings() {
    this.anonymousConsentsService.toggleSettingsVisible(false);
  }

  saveSettings(values: { [key: string]: boolean }) {
    Object.entries(values).forEach((value) => {
      if (value[1]) {
        this.anonymousConsentsService.giveConsent(value[0]);
      } else {
        this.anonymousConsentsService.withdrawConsent(value[0]);
      }
    });
    this.hideSettings();
  }
}
